import React, { useEffect, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Booking, Meta } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

function fmtDate(iso) {
    const d = new Date(iso);
    return {
        dow: d.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase(),
        date: d.toLocaleDateString("en-US", { month: "short", day: "2-digit" }).toUpperCase(),
        time: d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
    };
}

function groupByDay(slots) {
    const map = new Map();
    for (const s of slots) {
        const d = new Date(s.starts_at);
        const key = d.toISOString().slice(0, 10);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(s);
    }
    return Array.from(map.entries()).sort();
}

export default function Book() {
    const [slots, setSlots] = useState([]);
    const [selected, setSelected] = useState(null);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [notes, setNotes] = useState("");
    const [loading, setLoading] = useState(false);
    const [price, setPrice] = useState(250);

    useEffect(() => {
        Booking.slots().then(setSlots).catch(() => {});
        Meta.get().then((m) => m?.session_price_usd && setPrice(m.session_price_usd)).catch(() => {});
    }, []);

    const reserve = async (e) => {
        e.preventDefault();
        if (!selected) {
            toast.error("Pick a time slot first.");
            return;
        }
        setLoading(true);
        try {
            const res = await Booking.checkout({
                slot_id: selected,
                name,
                email,
                notes,
                origin_url: window.location.origin,
            });
            if (res.url) window.location.href = res.url;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not start checkout.");
            setLoading(false);
        }
    };

    const grouped = groupByDay(slots);

    return (
        <div className="min-h-screen" data-testid="book-page">
            <Header />
            <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-10 pb-16">
                <div className="panel p-8 md:p-12">
                    <div className="overline-cobalt mb-5">§ 1:1 Session · Checkout</div>
                    <h1
                        className="font-display"
                        style={{
                            fontSize: "clamp(40px, 5.6vw, 80px)",
                            lineHeight: 0.96,
                            color: "var(--ink)",
                        }}
                    >
                        Reserve a{" "}
                        <span
                            style={{
                                fontFamily: "'Fraunces', serif",
                                fontStyle: "italic",
                                fontWeight: 400,
                                color: "var(--cobalt)",
                            }}
                        >
                            private
                        </span>{" "}
                        session.
                    </h1>
                    <p
                        className="mt-5 max-w-2xl"
                        style={{ color: "var(--ink-2)", fontSize: 15, lineHeight: 1.65 }}
                    >
                        A 30-minute private call with Atlas. Pick a slot, tell us what
                        you want to cover, and complete payment via Stripe. You'll
                        receive a calendar invite the moment your payment confirms.
                    </p>
                </div>

                <div className="mt-5 grid md:grid-cols-12 gap-5">
                    {/* Slots */}
                    <div className="md:col-span-7">
                        <div className="panel p-5 mb-4 flex items-center justify-between">
                            <span className="overline-cobalt">[Step 01] Pick a time</span>
                            <span className="font-mono-tech text-[12px]" style={{ color: "var(--ink)" }}>
                                USD <b style={{ color: "var(--cobalt)" }}>${Math.round(price)}</b>
                            </span>
                        </div>

                        {grouped.length === 0 ? (
                            <div className="panel p-10" style={{ color: "var(--ink-2)" }} data-testid="no-slots">
                                No open slots right now. New availability is released
                                weekly — subscribe to the newsletter to get first access.
                            </div>
                        ) : (
                            <div className="panel divide-y" style={{ borderColor: "var(--rule)" }} data-testid="slot-list">
                                {grouped.map(([day, daySlots]) => {
                                    const first = fmtDate(daySlots[0].starts_at);
                                    return (
                                        <div
                                            key={day}
                                            className="grid grid-cols-12 gap-4 p-5"
                                            style={{ borderColor: "var(--rule-soft)" }}
                                        >
                                            <div className="col-span-3 md:col-span-2">
                                                <div className="overline-cobalt">{first.dow}</div>
                                                <div
                                                    style={{
                                                        fontFamily: "'Inter', sans-serif",
                                                        fontWeight: 900,
                                                        fontSize: 26,
                                                        color: "var(--ink)",
                                                        letterSpacing: "-0.02em",
                                                        lineHeight: 1,
                                                        marginTop: 4,
                                                    }}
                                                >
                                                    {first.date}
                                                </div>
                                            </div>
                                            <div className="col-span-9 md:col-span-10 flex flex-wrap gap-2">
                                                {daySlots.map((s) => {
                                                    const t = fmtDate(s.starts_at);
                                                    const isSel = selected === s.id;
                                                    return (
                                                        <button
                                                            key={s.id}
                                                            type="button"
                                                            onClick={() => setSelected(s.id)}
                                                            className="px-4 py-2.5 font-mono-tech text-[11px] tracking-[0.12em] border transition-colors"
                                                            style={{
                                                                background: isSel ? "var(--cobalt)" : "transparent",
                                                                color: isSel ? "white" : "var(--ink)",
                                                                borderColor: isSel ? "var(--cobalt)" : "var(--rule)",
                                                            }}
                                                            data-testid={`slot-${s.id}`}
                                                        >
                                                            {t.time}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Form */}
                    <form
                        onSubmit={reserve}
                        className="md:col-span-5 panel p-7 md:p-8 h-fit"
                        data-testid="booking-form"
                    >
                        <div className="overline-cobalt mb-5">[Step 02] Your details</div>
                        <input
                            type="text"
                            placeholder="Full name"
                            className="input-tech mb-3"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            data-testid="book-name-input"
                        />
                        <input
                            type="email"
                            placeholder="Email"
                            className="input-tech mb-3"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            data-testid="book-email-input"
                        />
                        <textarea
                            placeholder="What do you want to cover?"
                            rows={5}
                            className="input-tech mb-6"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            data-testid="book-notes-input"
                        />
                        <div className="flex items-end justify-between mb-5 pb-5" style={{ borderBottom: "1px dashed var(--rule)" }}>
                            <div className="overline">Total</div>
                            <div
                                style={{
                                    fontFamily: "'Inter', sans-serif",
                                    fontWeight: 900,
                                    fontSize: 36,
                                    color: "var(--cobalt)",
                                    letterSpacing: "-0.02em",
                                }}
                                data-testid="book-total"
                            >
                                ${Math.round(price)}
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={loading || !selected}
                            className="btn-primary w-full justify-center"
                            data-testid="book-pay-btn"
                        >
                            {loading ? "Redirecting…" : "Pay with Stripe"} <ArrowRight size={14} />
                        </button>
                        <p className="overline mt-4">Secured by Stripe · Test mode</p>
                    </form>
                </div>
            </section>
            <Footer />
        </div>
    );
}
